import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  CheckCircle,
  Info,
  Warning,
  TrendingUp,
  LocalOffer,
  Category,
  Visibility,
  Add,
  ArrowForward,
} from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';
import { getServiceById } from '../../config/services.config';

export interface ServiceStat {
  label: string;
  value: string | number;
  icon?: React.ReactNode;
  color?: string;
  trend?: 'up' | 'down' | 'neutral';
}

export interface ServiceAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

export interface ServiceFeature {
  title: string;
  description: string;
  icon?: React.ReactNode;
  available: boolean;
}

interface ServiceDashboardProps {
  serviceId: string;
  title?: string;
  description?: string;
  stats?: ServiceStat[];
  recentItems?: {
    title: string;
    subtitle?: string;
    status?: string;
    statusColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    actions?: ServiceAction[];
  }[];
  quickActions?: ServiceAction[];
  features?: ServiceFeature[];
  userTier?: 'free' | 'premium';
  upgradeMessage?: string;
  emptyStateMessage?: string;
  emptyStateAction?: ServiceAction;
}

const ServiceDashboard: React.FC<ServiceDashboardProps> = ({
  serviceId,
  title: customTitle,
  description: customDescription,
  stats = [],
  recentItems = [],
  quickActions = [],
  features = [],
  userTier = 'free',
  upgradeMessage,
  emptyStateMessage,
  emptyStateAction,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Get service configuration
  const serviceConfig = getServiceById(serviceId);
  const ServiceIcon = serviceConfig?.icon;
  
  // Use custom title/description if provided, otherwise use from config
  const title = customTitle || (serviceConfig ? t(serviceConfig.name) : '');
  const description = customDescription || (serviceConfig?.description ? t(serviceConfig.description) : '');

  return (
    <Box>
      {/* Service Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flex: 1 }}>
            {ServiceIcon && (
              <ServiceIcon sx={{ fontSize: 40, color: 'primary.main', mt: 0.5 }} />
            )}
            <Box>
              <Typography variant="h4" gutterBottom>
                {title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {description}
              </Typography>
            </Box>
          </Box>
          {userTier === 'premium' && (
            <Chip 
              label={t('services.premiumUser')} 
              color="primary" 
              variant="outlined"
            />
          )}
        </Box>
      </Paper>

      {/* Stats Section */}
      {stats.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {stats.map((stat, index) => (
            <Card key={index} sx={{ flex: '1 1 200px', minWidth: 200 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {stat.icon}
                  <Typography color="text.secondary" variant="body2" sx={{ ml: stat.icon ? 1 : 0 }}>
                    {stat.label}
                  </Typography>
                </Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    color: stat.color || 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  {stat.value}
                  {stat.trend && (
                    <TrendingUp 
                      sx={{ 
                        fontSize: 20,
                        color: stat.trend === 'up' ? 'success.main' : 'error.main',
                        transform: stat.trend === 'down' ? 'rotate(180deg)' : 'none'
                      }} 
                    />
                  )}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Recent Items / Activity */}
        <Box sx={{ flex: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('services.recentActivity')}
            </Typography>
            
            {recentItems.length > 0 ? (
              <List>
                {recentItems.map((item, index) => (
                  <React.Fragment key={index}>
                    {index > 0 && <Divider />}
                    <ListItem
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        py: 2,
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1">
                          {item.title}
                        </Typography>
                        {item.subtitle && (
                          <Typography variant="body2" color="text.secondary">
                            {item.subtitle}
                          </Typography>
                        )}
                        {item.status && (
                          <Chip
                            label={item.status}
                            size="small"
                            color={item.statusColor || 'default'}
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                      {item.actions && item.actions.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {item.actions.map((action, actionIndex) => (
                            <Button
                              key={actionIndex}
                              size="small"
                              variant={action.variant || 'text'}
                              color={action.color || 'primary'}
                              onClick={action.onClick}
                              startIcon={action.icon}
                            >
                              {action.label}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Alert severity="info">
                <Typography>
                  {emptyStateMessage || t('services.noRecentActivity')}
                </Typography>
                {emptyStateAction && (
                  <Button
                    size="small"
                    startIcon={emptyStateAction.icon || <Add />}
                    onClick={emptyStateAction.onClick}
                    sx={{ mt: 1 }}
                  >
                    {emptyStateAction.label}
                  </Button>
                )}
              </Alert>
            )}
          </Paper>
        </Box>

        {/* Quick Actions & Features */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Quick Actions */}
          {quickActions.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('services.quickActions')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    fullWidth
                    variant={action.variant || 'outlined'}
                    color={action.color || 'primary'}
                    startIcon={action.icon}
                    onClick={action.onClick}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {action.label}
                  </Button>
                ))}
              </Box>
            </Paper>
          )}

          {/* Features List */}
          {features.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('services.features')}
              </Typography>
              <List dense>
                {features.map((feature, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {feature.available ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Warning color="disabled" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={feature.title}
                      secondary={feature.description}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: feature.available ? 'text.primary' : 'text.disabled'
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        color: feature.available ? 'text.secondary' : 'text.disabled'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}

          {/* Upgrade Prompt for Free Users */}
          {userTier === 'free' && upgradeMessage && (
            <Paper 
              sx={{ 
                p: 3, 
                backgroundColor: 'primary.light', 
                color: 'primary.contrastText' 
              }}
            >
              <Typography variant="subtitle1" gutterBottom>
                {t('services.upgradeToPremium')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {upgradeMessage}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/app/pilot-registration')}
              >
                {t('services.upgradeNow')}
              </Button>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceDashboard;